import type { AvatarKey } from './constants.js';

/** Fases da máquina de estados de uma sala. */
export const GAME_PHASES = [
  'LOBBY',
  'DEALING',
  'READY',
  'COUNTDOWN',
  'PLAYING',
  'REFOCUS',
  'STAR_VOTE',
  'RESOLVING_STAR',
  'RESOLVING_ERROR',
  'LEVEL_COMPLETE',
  'CONNECTION_PAUSED',
  'GAME_WON',
  'GAME_LOST',
] as const;

export type GamePhase = (typeof GAME_PHASES)[number];

export type Card = {
  id: string;
  value: number;
};

export type RoomConfig = {
  capacity: number;
  maxLevel: number;
  startingLives: number;
  startingStars: number;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  /** Quando verdadeiro, nível máximo e vidas são recalculados no início da partida. */
  useRecommended: boolean;
};

export type PlayerPrivate = {
  id: string;
  sessionTokenHash: string;
  name: string;
  seat: number;
  connected: boolean;
  ready: boolean;
  hand: Card[];
  avatarKey: AvatarKey;
  joinedAt: number;
  disconnectedAt: number | null;
};

export type PublicPlayer = {
  id: string;
  name: string;
  seat: number;
  connected: boolean;
  ready: boolean;
  remainingCards: number;
  avatarKey: AvatarKey;
  isHost: boolean;
};

export type PlayedCard = {
  cardId: string;
  value: number;
  playerId: string;
  playedAt: number;
  intervalMs: number;
  causedError: boolean;
};

/** Carta revelada publicamente após um erro ou o uso de uma estrela. */
export type RevealedCard = {
  value: number;
  playerId: string;
  reason: 'error' | 'star';
  revealedAt: number;
};

export type TimerState = {
  running: boolean;
  /** Instante (epoch ms do servidor) em que o intervalo atual começou a contar. */
  intervalAnchor: number;
  /** Valor congelado do intervalo enquanto o cronômetro está pausado. */
  intervalFrozenMs: number;
  /** Instante em que a contagem do tempo total do nível foi retomada. */
  levelAnchor: number;
  /** Tempo de jogo efetivo já acumulado no nível. */
  levelAccumulatedMs: number;
};

export type PublicDragState = {
  playerId: string;
  /** Coordenadas normalizadas (0–1) relativas à área do tabuleiro. */
  x: number;
  y: number;
  updatedAt: number;
};

export type StarVoteState = {
  requestedBy: string;
  /** playerId → voto. Ausente significa que ainda não votou. */
  votes: Record<string, boolean>;
  expiresAt: number;
};

export type LevelStats = {
  level: number;
  cardsPlayed: number;
  errors: number;
  totalMs: number;
  averageIntervalMs: number;
};

export type GameStats = {
  levelsCompleted: number;
  cardsPlayed: number;
  errors: number;
  starsUsed: number;
  totalMs: number;
};

export type ErrorSummary = {
  playedValue: number;
  playedBy: string;
  livesRemaining: number;
  discarded: { playerId: string; values: number[] }[];
  at: number;
};

export type StarSummary = {
  requestedBy: string;
  starsRemaining: number;
  discarded: { playerId: string; values: number[] }[];
  at: number;
};

export type RewardSummary = {
  level: number;
  reward: 'star' | 'life';
  /** Verdadeiro quando a equipe já estava no máximo e o total não aumentou. */
  capped: boolean;
  at: number;
};

/** Estado interno completo da sala. Nunca é enviado ao cliente. */
export type RoomState = {
  id: string;
  code: string;
  hostPlayerId: string;
  phase: GamePhase;
  /** Fase de origem quando a sala entra em CONNECTION_PAUSED. */
  pausedFromPhase: GamePhase | null;
  config: RoomConfig;
  players: Map<string, PlayerPrivate>;
  level: number;
  lives: number;
  stars: number;
  pile: PlayedCard[];
  discardedCards: RevealedCard[];
  stateVersion: number;
  timerState: TimerState;
  activeDrags: Map<string, PublicDragState>;
  starVote: StarVoteState | null;
  countdownEndsAt: number | null;
  levelStats: { cardsPlayed: number; errors: number; intervals: number[] };
  gameStats: GameStats;
  lastError: ErrorSummary | null;
  lastStar: StarSummary | null;
  lastReward: RewardSummary | null;
  lastLevelSummary: LevelStats | null;
  createdAt: number;
  lastActivityAt: number;
};

/** Recorte público da sala, seguro para transmissão a todos os clientes. */
export type PublicRoomSnapshot = {
  code: string;
  hostPlayerId: string;
  phase: GamePhase;
  config: RoomConfig;
  players: PublicPlayer[];
  level: number;
  maxLevel: number;
  lives: number;
  maxLives: number;
  stars: number;
  maxStars: number;
  pile: PlayedCard[];
  topValue: number | null;
  playedCount: number;
  discardedCards: RevealedCard[];
  stateVersion: number;
  timer: TimerState & { serverNow: number };
  countdownEndsAt: number | null;
  starVote: StarVoteState | null;
  activeDrags: PublicDragState[];
  lastError: ErrorSummary | null;
  lastStar: StarSummary | null;
  lastReward: RewardSummary | null;
  lastLevelSummary: LevelStats | null;
  gameStats: GameStats;
  /** Prazo de reconexão do jogador desconectado há mais tempo. */
  reconnectDeadline: number | null;
  /** Verdadeiro quando o anfitrião já pode encerrar uma partida travada. */
  canEndGame: boolean;
};

/** Recorte privado, enviado apenas ao próprio jogador. */
export type PrivatePlayerSnapshot = {
  playerId: string;
  seat: number;
  avatarKey: AvatarKey;
  isHost: boolean;
  ready: boolean;
  hand: Card[];
  /** Menor carta da mão: a única jogável. */
  playableCardId: string | null;
  stateVersion: number;
};
